import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_API_URL;
const DETAIL_ENDPOINT = (id) => `${BASE_URL}/api/vendormanagement/${id}`;

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
};

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

const statusInfo = (v) => {
  if (!v) return { label: "-", bg: "#f3f4f6", color: "#6b7280", dot: "#9ca3af" };
  if (!v.status) return { label: "Suspended", bg: "#fef2f2", color: "#b91c1c", dot: "#ef4444" };
  if (!v.approval) return { label: "Pending approval", bg: "#fffbeb", color: "#92400e", dot: "#f59e0b" };
  return { label: "Approved", bg: "#ecfdf5", color: "#047857", dot: "#10b981" };
};

const Field = ({ label, children }) => (
  <div>
    <div style={{ fontSize: 12, color: "#8b8f99", marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 14.5, color: "#1a1d23", fontWeight: 500, lineHeight: 1.4 }}>
      {children ?? "-"}
    </div>
  </div>
);

const Pill = ({ active, label }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "5px 12px",
      borderRadius: 999,
      fontSize: 12.5,
      fontWeight: 600,
      background: active ? "#eef2ff" : "#f4f4f6",
      color: active ? "#4338ca" : "#9ca3af",
    }}
  >
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: active ? "#6366f1" : "#c8cad1",
      }}
    />
    {label}
  </span>
);

// Renders the certificate thumbnail, or a clear fallback if it fails to load
const CertificateImage = ({ url, vendorName }) => {
  const [failed, setFailed] = useState(false);

  if (!url) {
    return <span style={{ color: "#c8cad1", fontWeight: 400 }}>Not provided</span>;
  }

  if (failed) {
    return (
      <span style={{ color: "#b91c1c", fontWeight: 400, fontSize: 13 }}>
        Certificate file could not be loaded
      </span>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <img
        src={url}
        alt={`${vendorName} registration certificate`}
        style={{
          width: 160,
          height: 160,
          objectFit: "contain",
          border: "1px solid #eef0f4",
          borderRadius: 8,
          background: "#fafafa",
          cursor: "pointer",
        }}
        onError={() => setFailed(true)}
      />
    </a>
  );
};

const VendorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(DETAIL_ENDPOINT(id), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancelled) return;
        setVendor(res.data?.data || null);
      } catch (err) {
        if (cancelled) return;
        const message = err.response?.data?.message || "Failed to load vendor details";
        setError(message);
        toast.error(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div style={{ background: "#f6f7fb", minHeight: "100vh", padding: "28px 32px" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "100px 0", color: "#9ca3af" }}>
          <div
            style={{
              width: 28, height: 28, borderRadius: "50%",
              border: "3px solid #e5e7eb", borderTopColor: "#6362e7",
              animation: "spin 0.7s linear infinite",
            }}
          />
          <span style={{ fontSize: 13.5 }}>Loading vendor…</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div style={{ background: "#f6f7fb", minHeight: "100vh", padding: "28px 32px" }}>
        <button
          onClick={() => navigate(-1)}
          style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer", marginBottom: 20, fontSize: 13.5 }}
        >
          ← Back
        </button>
        <div
          style={{
            maxWidth: 420, margin: "60px auto 0", textAlign: "center",
            background: "#fff", border: "1px solid #eef0f4", borderRadius: 16, padding: "36px 28px",
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1d23", marginBottom: 6 }}>
            Couldn't load this vendor
          </div>
          <div style={{ fontSize: 13.5, color: "#8b8f99" }}>
            {error || "This vendor doesn't exist or may have been removed."}
          </div>
        </div>
      </div>
    );
  }

  const s = statusInfo(vendor);
  const photo = vendor.shopPhotos?.[0] ? `${BASE_URL}/uploads/vendors/${vendor.shopPhotos[0]}` : null;
  const certUrl = vendor.registrationCertificate
    ? `${BASE_URL}/uploads/vendors/${vendor.registrationCertificate}`
    : null;

  return (
    <div style={{ background: "#f6f7fb", minHeight: "100vh", padding: "28px 32px" }}>
      {/* ── Back ── */}
      <button
        onClick={() => navigate(-1)}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "8px 14px 8px 10px", borderRadius: 8, border: "1px solid #e5e7eb",
          background: "#fff", cursor: "pointer", marginBottom: 20, fontSize: 13.5, color: "#374151",
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Vendors
      </button>

      {/* ── Header band ── */}
      <div
        style={{
          background: "linear-gradient(135deg, #6362e7 0%, #837ff0 100%)",
          borderRadius: 20,
          padding: "28px 32px",
          display: "flex",
          alignItems: "center",
          gap: 20,
          marginBottom: 20,
          color: "#fff",
        }}
      >
        <div
          style={{
            width: 64, height: 64, borderRadius: 16, background: "rgba(255,255,255,0.18)",
            display: "flex", alignItems: "center", justifyContent: "center",
            overflow: "hidden", flexShrink: 0, fontSize: 22, fontWeight: 700,
            border: "1px solid rgba(255,255,255,0.3)",
          }}
        >
          {photo ? (
            <img
              src={photo}
              alt={vendor.vendorName}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            initials(vendor.vendorName)
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, lineHeight: 1.2 }}>
            {vendor.vendorName}
          </h1>
          <p style={{ margin: "6px 0 0", fontSize: 13, color: "rgba(255,255,255,0.85)" }}>
            Vendor since {formatDate(vendor.createdAt)} · UEN {vendor.uenNumber}
          </p>
        </div>

        <span
          style={{
            display: "flex", alignItems: "center", gap: 7,
            background: "rgba(255,255,255,0.16)", padding: "7px 16px", borderRadius: 999,
            fontSize: 12.5, fontWeight: 700, whiteSpace: "nowrap",
            border: "1px solid rgba(255,255,255,0.3)",
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: s.dot }} />
          {s.label}
        </span>
      </div>

      {/* ── Content grid ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 20, alignItems: "start" }}>
        {/* Contact / business info */}
        <div style={{ background: "#fff", border: "1px solid #eef0f4", borderRadius: 16, padding: 26 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1a1d23", marginBottom: 18 }}>
            Business details
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <Field label="UEN number">{vendor.uenNumber}</Field>
            <Field label="Mobile">{vendor.mobile}</Field>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Shop address">{vendor.shopAddress}</Field>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Registration certificate">
                <CertificateImage url={certUrl} vendorName={vendor.vendorName} />
              </Field>
            </div>
          </div>
        </div>

        {/* Placement / visibility */}
        <div style={{ background: "#fff", border: "1px solid #eef0f4", borderRadius: 16, padding: 26 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1a1d23", marginBottom: 18 }}>
            Storefront placement
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13.5, color: "#4b5563" }}>Featured vendor</span>
              <Pill active={!!vendor.featuredVendor} label={vendor.featuredVendor ? "On" : "Off"} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13.5, color: "#4b5563" }}>Best seller</span>
              <Pill active={!!vendor.bestSellerVendor} label={vendor.bestSellerVendor ? "On" : "Off"} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13.5, color: "#4b5563" }}>Shop open</span>
              <Pill active={vendor.shopStatus !== false} label={vendor.shopStatus !== false ? "Open" : "Closed"} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDetail;